// Diálogo modal no padrão Apple (substitui os confirm()/prompt() nativos do
// navegador). Baseado em Promise: define `window.Dialog`.
//   window.Dialog.confirm(message, opts) -> Promise<boolean>
//   window.Dialog.prompt(message, opts)  -> Promise<string|null>
//
// opts: { title, confirmLabel, cancelLabel, danger, placeholder, defaultValue,
//         inputType, required }
// Rótulos padrão localizados via <meta name="x-dialog-labels"> (ver _head_tags).
// Importado por application.js, form.js e draw.js. CSS em styles/_apple_toast.scss.

const LEAVE_FALLBACK_MS = 300

function dialogLabels () {
  return document.querySelector('meta[name="x-dialog-labels"]')?.dataset || {}
}

function buildDialog (kind, message, opts) {
  const labels = dialogLabels()

  const overlay = document.createElement('div')

  overlay.className = 'apple-dialog-overlay'

  const card = document.createElement('div')

  card.className = 'apple-dialog'
  card.setAttribute('role', 'dialog')
  card.setAttribute('aria-modal', 'true')

  if (opts.title) {
    const titleEl = document.createElement('p')

    titleEl.className = 'apple-dialog__title'
    titleEl.textContent = opts.title

    card.appendChild(titleEl)
  }

  if (message) {
    const messageEl = document.createElement('p')

    messageEl.className = 'apple-dialog__message'
    messageEl.textContent = message

    card.appendChild(messageEl)
  }

  let input = null

  if (kind === 'prompt') {
    input = document.createElement('input')
    input.type = opts.inputType || 'text'
    input.className = 'apple-dialog__input base-input'

    if (opts.placeholder) input.placeholder = opts.placeholder
    if (opts.defaultValue) input.value = opts.defaultValue

    card.appendChild(input)
  }

  const actions = document.createElement('div')

  actions.className = 'apple-dialog__actions'

  const cancel = document.createElement('button')

  cancel.type = 'button'
  cancel.className = 'apple-dialog__btn apple-dialog__btn--cancel'
  cancel.textContent = opts.cancelLabel || labels.cancel || 'Cancel'

  const confirm = document.createElement('button')

  confirm.type = 'button'
  confirm.className = `apple-dialog__btn apple-dialog__btn--confirm${opts.danger ? ' apple-dialog__btn--danger' : ''}`
  confirm.textContent = opts.confirmLabel || (opts.danger ? labels.danger : labels.confirm) || 'OK'

  // Validação de prompt obrigatório: confirma só com valor não-vazio.
  const required = kind === 'prompt' && opts.required

  if (required) {
    confirm.disabled = !input.value.trim()

    input.addEventListener('input', () => { confirm.disabled = !input.value.trim() })
  }

  actions.appendChild(cancel)
  actions.appendChild(confirm)
  card.appendChild(actions)
  overlay.appendChild(card)

  return { overlay, card, input, cancel, confirm, required }
}

function open (kind, message, opts = {}) {
  return new Promise((resolve) => {
    const lastFocus = document.activeElement
    const { overlay, card, input, cancel, confirm, required } = buildDialog(kind, message, opts)

    document.body.appendChild(overlay)

    requestAnimationFrame(() => overlay.classList.add('apple-dialog-overlay--open'))

    let settled = false

    const close = (result) => {
      if (settled) return
      settled = true

      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('keyup', onKeyUp, true)

      overlay.classList.remove('apple-dialog-overlay--open')
      overlay.classList.add('apple-dialog-overlay--leave')

      let done = false

      const remove = () => {
        if (done) return
        done = true

        overlay.remove()

        if (lastFocus && lastFocus.focus) lastFocus.focus()
      }

      // Só a transição do próprio overlay encerra (transitionend de botões/input
      // borbulha e removeria cedo).
      const onEnd = (e) => {
        if (e.target !== overlay) return

        overlay.removeEventListener('transitionend', onEnd)
        remove()
      }

      overlay.addEventListener('transitionend', onEnd)
      setTimeout(remove, LEAVE_FALLBACK_MS)

      resolve(result)
    }

    const onConfirm = () => {
      if (required && !input.value.trim()) {
        input.focus()

        return
      }

      close(kind === 'prompt' ? (input ? input.value : '') : true)
    }

    const onCancel = () => close(kind === 'prompt' ? null : false)

    confirm.addEventListener('click', onConfirm)
    cancel.addEventListener('click', onCancel)
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) onCancel() })

    // Escape/Enter tratados em CAPTURA: o keydown suprime ação nativa (submit do
    // form, ativação do botão); o keyup decide e PÁRA a propagação, para o Escape
    // não vazar para o turbo-modal/handler global (que escutam keyup) e fechar a
    // tela por baixo.
    const onKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    const onKeyUp = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onCancel()
      } else if (e.key === 'Enter' && document.activeElement !== cancel) {
        e.preventDefault()
        e.stopPropagation()
        onConfirm()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('keyup', onKeyUp, true)

    // Prende o Tab dentro do card (foco modal).
    card.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return

      const focusables = card.querySelectorAll('button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')

      if (!focusables.length) return

      const first = focusables[0]
      const last = focusables[focusables.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    })

    requestAnimationFrame(() => (input || confirm).focus())
  })
}

if (!window.Dialog) {
  window.Dialog = {
    confirm: (message, opts = {}) => open('confirm', message, opts),
    prompt: (message, opts = {}) => open('prompt', message, opts)
  }
}
